import React, { useState, useEffect, ChangeEvent } from 'react';
import SubstanceSearchModal from '../SubstanceSearchModal';
import { v4 as uuidv4 } from 'uuid';

interface IngredientsTableProps {
    columns: string[];
    localizedFriendlyNameMap: Readonly<{ [columnId: string]: string }>;
    rows: string[][];
    editor?: boolean;
    buttonText?: string;
    onResponseChange: (value: any) => void;
}

enum InventoryColumnIdType {
    Casrn = "casrn",
    ChemicalName = "chemical-name",
    Proprietary = "proprietary"
}

const IngredientsTable: React.FC<IngredientsTableProps> = ({ columns, rows, editor = true, buttonText, localizedFriendlyNameMap, onResponseChange }) => {
    const [rowsValue, setRowsValue] = useState(rows);
    const [substanceColIndex, setSubstanceColIndex] = useState(3);
    const [uniqueId] = useState(uuidv4());
    
    const handleInputChange = (rowIndex: number, columnIndex: number, event: ChangeEvent<HTMLInputElement>) => {
        if (rowsValue) {
            let updatedMatrix = [...rowsValue];
            updatedMatrix[rowIndex][columnIndex] = event.target.value;
            setRowsValue(updatedMatrix);
            onResponseChange(updatedMatrix);
        }
    }

    const deleteRow = (rowIndex: number) => {
        if (rowsValue) {
            let updatedRows = rowsValue.filter((_, index) => index !== rowIndex);
            setRowsValue(updatedRows);
            onResponseChange(updatedRows);
        }
    }

    const addRow = () => {
        let newRow = Array(columns.length).fill('');
        if (rowsValue) {
            let updatedRows = [...rowsValue, newRow];
            setRowsValue(updatedRows);
            onResponseChange(updatedRows);
        }
        else {
            setRowsValue(newRow);
            onResponseChange(newRow);
        }
    }

    const trimMatrix = (row: string[]) => {
        if (row.length < columns.length) {
            // push empty cells to fill gap
            for (let i = columns.length - row.length; i-- > 0;) {
                row.push("");
            }
        }
        else if (row.length > columns.length) {
            // clip overflow cells
            while (row.length > columns.length)
                row.pop();
        }
    }

    const addSubstance = (colIndex: number, e: React.MouseEvent<HTMLButtonElement>) => {
        setSubstanceColIndex(colIndex);
    }

    const substanceAdded = (casrn: string, chemicalName: string) => {
        if (rowsValue) {
            let updatedMatrix = [...rowsValue];
            updatedMatrix[substanceColIndex][0] = casrn;
            updatedMatrix[substanceColIndex][1] = chemicalName;
            setRowsValue(updatedMatrix);
            onResponseChange(updatedMatrix);
        }
    }

    return (
        <div className="generic-table-parent data-table-parent">
            <p><button className="btn btn-default add-data-table-row-button" onClick={addRow} disabled={!editor}>{buttonText ?? "Add New Row"}</button></p>
            <div className="table-container">
                <table className="table toxnot-table-v2">
                    <thead>
                        <tr>
                            {columns.map((columnValue, key) => <th key={key}>{localizedFriendlyNameMap[columnValue] ?? columnValue} 
                                {columns[key] === InventoryColumnIdType.ChemicalName || columns[key] === InventoryColumnIdType.Casrn ? <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span> : ""}
                            </th>)
                            }
                            {editor ? (<th></th>) : null}
                        </tr>
                    </thead>
                    <tbody data-data-table-index={uniqueId}>
                        {rowsValue?.map((row, rowIndex) => {
                            trimMatrix(row)
                            return (<tr key={rowIndex}>
                                {row?.map((colValue, colIndex) => {
                                    if ((columns[colIndex] === InventoryColumnIdType.Casrn) || (columns[colIndex] === InventoryColumnIdType.ChemicalName))
                                        return (<td key={colIndex}>
                                            {editor ? <button type="button" className="btn btn-default btn-sm edit-substance-button" onClick={(e) => addSubstance(rowIndex, e)} data-toggle="modal" data-target={"#substance-search-modal_"+uniqueId}><span className="glyphicon glyphicon-pencil"></span></button> : ""} <span>{colValue}</span>
                                        </td>)
                                    else
                                        return(<td key={colIndex}>
                                            <input type="text" onChange={(event) => handleInputChange(rowIndex, colIndex, event)} className="form-control question-answer-data-table-cell-input" value={colValue} disabled={!editor}></input>
                                        </td>)
                                })}
                                {editor ? (
                                    <td>
                                        <button className="btn btn-toxnot-danger remove-data-table-row-button" data-table-row-index={rowIndex} onClick={() => deleteRow(rowIndex)}><span className="glyphicon glyphicon-remove"></span></button>
                                    </td>)
                                    : null}
                            </tr>)
                        })}
                    </tbody>
                </table>
            </div>
            <SubstanceSearchModal modalId={"substance-search-modal_"+uniqueId} modalTitle="Assign a Substance" onSubstanceChosen={substanceAdded}></SubstanceSearchModal>
        </div>
    )
}

export default IngredientsTable;